import type JhaButton from '@banno/jha-wc/src/buttons/jha-button/jha-button';
import { InjectProperty } from '@jkhy/frontend-utils/di';
import { delay, exists } from '@jkhy/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { ObservationSource } from '@jkhy/frontend-utils/observable';
import { type Action } from '@jkhy/frontend-utils/types';
import type JhPlatformDrawer from '@jkhy/platform-tools/components/jh-platform-drawer';
import { WindowService } from '@treasury/utils';
import { waitForAnimations } from '@treasury/utils/functions';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import {
    getContentFromSlot,
    ISideDrawerContent,
    isSideDrawerContent,
    listenForDrawerResult,
    SideDrawerButton,
    SideDrawerButtonPosture,
} from './side-drawer.service.types';

// eslint-disable-next-line no-duplicate-imports
import '@banno/jha-wc/src/buttons/jha-button/jha-button';
// eslint-disable-next-line no-duplicate-imports
import '@jkhy/platform-tools/components/jh-platform-drawer';

export const tagName = 'tm-side-drawer';

@customElement(tagName)
export class TmSideDrawer extends LitElement {
    @property()
    public heading = '';

    @property({
        type: String,
        reflect: true,
    })
    public readonly alignment: 'right' | 'left' = 'right';

    @property({
        type: String,
        reflect: true,
    })
    public readonly footerAlignment: 'right' | 'left' = 'right';

    @property({
        type: Boolean,
    })
    public readonly closeOnBlur = true;

    @property({
        attribute: false,
        reflect: false,
    })
    public buttons: SideDrawerButton[] = [];

    /** Called before the drawer closes. Return `true` to allow, `false` to cancel. */
    @property({ attribute: false })
    public onBeforeClose?: () => Promise<boolean>;

    public isHandlingClose = false;

    public get closeComplete() {
        if (!this.drawer) {
            throw new Error('Could not determine closed state');
        }

        return this.waitForAnimations();
    }

    /**
     * A promise that resolves when the drawer is ready for interaction.
     *
     * Exists mostly to support testing scenarios.
     */
    public get readyForInteraction() {
        return this.interactivitySource.toPromise(false);
    }

    @InjectProperty()
    declare private readonly window: WindowService;

    @state()
    private closed = false;

    @state()
    private data?: unknown;

    /**
     * Indicates whether the drawer is ready to interact with user input.
     *
     * Used to determine if clicks outside the drawer should close it.
     */
    private isInteractive = false;

    private readonly interactivitySource = new ObservationSource<void>();

    private drawer?: JhPlatformDrawer;

    private readonly slotRef = createRef<HTMLSlotElement>();

    private readonly buttonContainerRef = createRef<Element>();

    private readonly disconnectedTasks: Action[] = [];

    private drawerHandle?: ReturnType<typeof listenForDrawerResult>;

    private contentProvider?: ISideDrawerContent;

    public firstUpdated(changedProps: PropertyValues): void {
        super.firstUpdated(changedProps);
        this.listenForBlur();
    }

    public disconnectedCallback() {
        this.disconnectedTasks.forEach(task => task());
        this.drawerHandle?.unsubscribe();
        this.drawerHandle = undefined;
    }

    /**
     * Close the drawer programmatically from an external source.
     */
    public close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        this.closed = true;
        data = data ?? (this.data as T);

        const result = {
            data,
            reason,
        };

        const closeEvent = new CustomEvent('close-drawer', {
            detail: result,
        });
        this.dispatchEvent(closeEvent);
    }

    public getButtons() {
        const { value: buttonContainer } = this.buttonContainerRef;
        if (!buttonContainer) {
            throw new Error('Could not get side drawer buttons');
        }

        return Array.from(buttonContainer.querySelectorAll<JhaButton>('jha-button'));
    }

    public render() {
        const conditionalClasses = classMap({
            [`slide-in-${this.alignment}`]: !this.closed,
            [`slide-out-${this.alignment}`]: this.closed,
        });

        const overlayClasses = classMap({
            overlay: true,
            'fade-in': !this.closed,
            'fade-out': this.closed,
        });

        const overlay = !this.closeOnBlur ? html`<div class="${overlayClasses}"></div>` : '';

        return html`${overlay}<jh-platform-drawer
                ${ref(drawer => {
                    this.drawer = drawer as JhPlatformDrawer;
                    this.listenForResult();
                })}
                class="${conditionalClasses} max-w-full"
                .heading=${this.heading}
                @close-drawer=${async (e: Event) => {
                    if (this.isHandlingClose) {
                        e.stopPropagation();
                        return;
                    }
                    if (this.onBeforeClose) {
                        e.stopPropagation();
                        this.isHandlingClose = true;
                        const confirmed = await this.onBeforeClose();
                        this.isHandlingClose = false;
                        if (!confirmed) {
                            return;
                        }
                        this.close(DynamicComponentExitReason.Cancel);
                        return;
                    }
                    this.closed = true;
                }}
            >
                ${this.contentProvider?.drawerHeaderTemplate
                    ? html`<div slot="drawer-header">
                          ${this.contentProvider.drawerHeaderTemplate}
                      </div>`
                    : ''}
                <slot ${ref(this.slotRef)} @slotchange=${() => this.onSlotChange()}></slot>
                <div
                    slot="footer"
                    id="footer"
                    class=${classMap({
                        'flex flex-row': true,
                        'justify-end': this.footerAlignment === 'right',
                        'justify-start': this.footerAlignment === 'left',
                    })}
                >
                    ${this.renderButtons()}
                </div>
            </jh-platform-drawer>`;
    }

    private listenForResult() {
        if (!this.drawer) {
            return;
        }

        if (this.drawerHandle) {
            this.drawerHandle.unsubscribe();
            this.drawerHandle = undefined;
        }

        this.drawerHandle = listenForDrawerResult(this.drawer, result => {
            this.data = result;
        });
    }

    private onSlotChange() {
        const { value: slot } = this.slotRef;

        if (!slot) {
            return;
        }

        const content = getContentFromSlot(slot);
        if (!exists(content)) {
            return;
        }

        if (isSideDrawerContent(content)) {
            this.hydrateFromContentProvider(content);

            // keep drawer chrome in sync with its content
            // in case label state depends on content state
            const { unsubscribe } = content.updated$.subscribe(() => this.requestUpdate());

            this.disconnectedTasks.push(unsubscribe);
        }

        // introduce a slight delay so that the drawer does not immediately
        // closes when the UI element to open it is clicked
        this.waitForAnimations()
            .then(() => delay(100))
            .then(() => {
                this.isInteractive = true;
                this.interactivitySource.emit();
            });
    }

    private hydrateFromContentProvider(content: ISideDrawerContent) {
        const { drawerButtons = [] } = content;
        const merged = [...this.buttons, ...drawerButtons];

        this.buttons = merged;
        this.contentProvider = content;
    }

    private renderButtons() {
        const buttonTemplates = this.buttons.map(b => {
            const { onClick, checkEnabled, reason, label: labelOrFunc } = b;
            const isEnabled = checkEnabled ? checkEnabled(this.data) : true;
            const buttonRef = createRef<JhaButton>();
            // if no click handler is provided, buttons will close the drawer by default
            const clickHandler = async () => {
                try {
                    if (!onClick) {
                        this.close(reason);
                    } else {
                        await onClick(
                            data => this.close(reason, data),
                            data => (this.data = data)
                        );
                    }
                } finally {
                    if (buttonRef.value) {
                        // if the button is sync, it will be in a loading state
                        buttonRef.value.isLoading = false;
                    }
                }
            };
            const label = typeof labelOrFunc === 'string' ? labelOrFunc : labelOrFunc();

            return html`<jha-button
                ${ref(buttonRef)}
                ?primary=${b.posture === SideDrawerButtonPosture.Primary}
                ?outline-primary=${b.posture === SideDrawerButtonPosture.Secondary}
                ?tertiary=${b.posture === SideDrawerButtonPosture.Tertiary}
                ?success=${b.posture === SideDrawerButtonPosture.Constructive}
                ?danger=${b.posture === SideDrawerButtonPosture.Destructive}
                class="drawer-button"
                ?disabled=${!isEnabled}
                @click=${() => clickHandler()}
                >${label}</jha-button
            >`;
        });

        // place button templates in a container div so that they can be queried easily
        return html`<div ${ref(this.buttonContainerRef)}>${buttonTemplates}</div>`;
    }

    private listenForBlur() {
        if (!this.closeOnBlur) {
            return;
        }

        const { unsubscribe } = this.window.addEventListener('click', e => this.handleBlur(e));
        this.disconnectedTasks.push(unsubscribe);
    }

    /**
     * Interrogates all click events on the window to determine if the user clicked outside
     * of the drawer when `closeOnBlur` is enabled.
     *
     * @param e Click event enacted anywhere on the window.
     */
    private async handleBlur(e: Event) {
        if (!this.drawer || !this.isInteractive || this.closed || this.isHandlingClose) {
            return;
        }

        const eventPath = e.composedPath();
        if (eventPath.includes(this)) {
            return;
        }

        // Dropdowns/date-pickers opened by the drawer's content (vaadin-select, combo-box,
        // date-picker, etc.) render their options in a floating *-overlay in the top layer,
        // outside the drawer's DOM. A click there is still an in-drawer interaction, so don't
        // treat it as a blur — otherwise selecting a filter value closes the whole drawer.
        const clickedInOverlay = eventPath.some(
            node => node instanceof Element && node.localName.endsWith('-overlay')
        );
        if (clickedInOverlay) {
            return;
        }

        if (this.onBeforeClose) {
            this.isHandlingClose = true;
            const confirmed = await this.onBeforeClose();
            this.isHandlingClose = false;
            if (!confirmed) {
                return;
            }
        }

        this.close(DynamicComponentExitReason.Cancel);
    }

    private async waitForAnimations() {
        if (!this.drawer) {
            return;
        }

        await waitForAnimations(this.drawer);
    }

    public static get styles() {
        return css`
            :host {
                display: flex;
                height: 100%;
                max-width: 100%;
                --jh-platform-drawer-width: auto;
            }
            jh-platform-drawer {
                --jh-color-container-page: var(--container-primary, #e0e0e0);
                --jh-color-divider-primary: var(--divider-primary, #e0e0e0);
                --jh-color-content-primary-enabled: var(--primary-text-color, #000000);
                --icon-size: 24px;
                --jh-icon-size-medium: 24px;
                --jh-size-600: 24px;
                color: var(--primary-text-color, #000000);
            }
            @media (prefers-color-scheme: dark) {
                /* No color override here: the unconditional jh-platform-drawer rule above
                   uses --container-primary / --divider-primary, which already flip to the
                   navy dark theme. The old grey override made drawers clash with the app. */
                .drawer-button {
                    --jha-button-disabled-opacity: 0.7;
                }
            }
            #footer {
                display: flex;
                flex-direction: row;
            }
            .drawer-button {
                padding: 4px;
                /* Filled buttons (primary/success/danger/etc.) sit on saturated brand/semantic
                   backgrounds, so their label must stay light. The app maps --jha-button-text to
                   a token that resolves dark in the html[theme='dark'] theme, which we can't reach
                   from inside this shadow root, so pin it to the theme-aware light content color. */
                --jha-button-text: var(--content-light, #fff);
            }
            [slot='drawer-header'] {
                display: flex;
                align-items: center;
            }
            [slot='drawer-header'] .drawer-heading {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                font-size: 1.17em;
                font-weight: bold;
            }
            [slot='drawer-header'] .drawer-heading-stack {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }
            [slot='drawer-header'] .drawer-subheading {
                font-size: 0.875rem;
                font-weight: 400;
                color: var(--secondary-text-color, #6b6c6f);
            }
            .slide-in-right {
                animation: slide-in-right 0.5s;
            }

            .slide-out-right {
                animation: slide-out-right 1s forwards;
            }

            .slide-in-left {
                animation: slide-in-left 0.5s;
            }

            .slide-out-left {
                animation: slide-out-left 1s forwards;
            }

            @keyframes slide-in-left {
                from {
                    translate: -150vw 0;
                    display: none;
                }

                to {
                    translate: 0 0;
                }
            }

            @keyframes slide-out-left {
                from {
                    translate: 0 0;
                }

                to {
                    translate: -150vw 0;
                    display: none;
                }
            }

            @keyframes slide-in-right {
                from {
                    translate: 150vw 0;
                    display: none;
                }

                to {
                    translate: 0 0;
                }
            }

            @keyframes slide-out-right {
                from {
                    translate: 0 0;
                }

                to {
                    translate: 150vw 0;
                    display: none;
                }
            }

            .overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(0, 0, 0, 0.5);
                z-index: -1;
            }

            .fade-in {
                animation: fade-in 0.3s ease-in;
            }

            .fade-out {
                animation: fade-out 0.3s ease-out forwards;
            }

            @keyframes fade-in {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }

            @keyframes fade-out {
                from {
                    opacity: 1;
                }
                to {
                    opacity: 0;
                }
            }
        `;
    }
}
